import { Accordion, AccordionDetails, AccordionSummary, Divider, Grid, Typography } from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { useMemo } from 'react'
import { useQuoteLines } from '../../lines/QuoteLinesContext'
import { flattenBundledStructure, formatCurrency } from '../../../helper'

const formatPercent = (value: number) => `${(value * 100).toFixed(2)}%`

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <div className="quote-summary-row">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </div>
)

const QuoteSummary = () => {
  const { quote, lineItems } = useQuoteLines()

  const msrpTotal = useMemo(
    () =>
      flattenBundledStructure(lineItems)
        .filter(line => line.LineNumber !== -1)
        .reduce((sum, line) => sum + (line.MSRP ?? 0) * (line.Qty ?? 0), 0),
    [lineItems],
  )

  const markup = quote.TotalCost ? (quote.Subtotal - quote.TotalCost) / quote.TotalCost : 0

  return (
    <Accordion disableGutters defaultExpanded className="quote-summary-accordion">
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Quote Summary</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing="24px">
          <Grid size="grow" className="quote-summary-column">
            <Typography variant="overline">Pricing</Typography>
            <SummaryRow label="MSRP Total" value={formatCurrency(msrpTotal)} />
            <SummaryRow label="Subtotal" value={formatCurrency(quote.Subtotal)} />
            <SummaryRow label="Tax" value={formatCurrency(quote.Tax)} />
            <SummaryRow label="Shipping" value={formatCurrency(quote.ShippingHandling)} />
            <Divider />
            <SummaryRow label="Grand Total" value={formatCurrency(quote.GrandTotal)} />
          </Grid>
          <Divider orientation="vertical" flexItem />
          <Grid size="grow" className="quote-summary-column">
            <Typography variant="overline">Costs & Fees</Typography>
            <SummaryRow label="Total Cost (Sum of Ext Costs)" value={formatCurrency(quote.TotalCost)} />
            <SummaryRow label="Contract Fee" value={formatCurrency(quote.ContractFees)} />
            <SummaryRow label="Partner Fee" value={formatCurrency(quote.PartnerFees)} />
            <SummaryRow label="Credit Card Fee" value={formatCurrency(quote.CreditCardFees)} />
            <Divider />
            <SummaryRow label="Total Fee" value={formatCurrency(quote.TotalFees)} />
          </Grid>
          <Divider orientation="vertical" flexItem />
          <Grid size="grow" className="quote-summary-column">
            <Typography variant="overline">Profitability</Typography>
            <SummaryRow label="Gross Profit ($)" value={formatCurrency(quote.GrossProfit)} />
            <SummaryRow label="Gross Profit (%)" value={formatPercent(quote.GrossProfitPercentage)} />
            <SummaryRow label="Markup (%)" value={formatPercent(markup)} />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

export default QuoteSummary
